import { useEffect, useRef } from 'react';
import { isIOS, isAndroid, isNative } from '../utils/platform';
import { syncHealthSnapshots, backfillHealthSnapshots } from '../services/deviceContext';
import { silentFetch } from '../services/api';
import { isBriefingReminderScheduled, scheduleBriefingReminder } from '../services/notifications';
import useAppStore from '../stores/appStore';

// One-time native bootstrap. All Capacitor imports dynamic with try/catch
// so the web build never breaks (shared-docs/capacitor-native-setup.md).
export default function useNativeSetup() {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        if (isIOS() || isAndroid()) {
          const { StatusBar, Style } = await import('@capacitor/status-bar');
          await StatusBar.setStyle({ style: Style.Light });
          if (isAndroid()) await StatusBar.setBackgroundColor({ color: '#ffffff' });
        }

        if (isNative()) {
          const { SplashScreen } = await import('@capacitor/splash-screen');
          await SplashScreen.hide();
        }

        if (isAndroid()) {
          const { App } = await import('@capacitor/app');
          App.addListener('backButton', ({ canGoBack }) => {
            if (canGoBack) window.history.back();
            else App.exitApp();
          });
        }

        if (isNative()) {
          const { App } = await import('@capacitor/app');
          App.addListener('appUrlOpen', async ({ url }) => {
            if (url?.includes('/auth/callback')) {
              const { Browser } = await import('@capacitor/browser');
              await Browser.close();
            }
            // Bank-connect return (custom scheme from /banking/callback):
            // close the Safari sheet, route the SPA to Money (history API —
            // no reload) and let MoneyPage's auto-confirm complete the
            // connection.
            if (url?.includes('banking/callback')) {
              try {
                const { Browser } = await import('@capacitor/browser');
                await Browser.close();
              } catch { /* sheet may already be closed */ }
              if (window.location.pathname !== '/money') {
                window.history.pushState({}, '', '/money');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
              window.dispatchEvent(new CustomEvent('cadence:bank-callback'));
            }
          });
          // Background push of daily health snapshots (silent, non-blocking).
          // The 2-day sync runs first so today's numbers land immediately;
          // the one-time 30-day history backfill follows in the background
          // (idempotent — skips dates the server already has). Existing users
          // get the backfill on their first launch after this update.
          (async () => {
            try {
              await syncHealthSnapshots();
              // Only after the user has been through the permission flow —
              // before that every day reads as zero-signal and the one-shot
              // flag would burn for nothing. HealthPage runs the backfill
              // itself right after a fresh grant.
              const { healthBackfillDone, healthPermissionAsked } = useAppStore.getState();
              if (!healthBackfillDone && healthPermissionAsked) {
                const ok = await backfillHealthSnapshots(30);
                if (ok) useAppStore.getState().setHealthBackfillDone(true);
              }
            } catch { /* silent — retried next launch */ }
          })();
          // If a daily briefing reminder is pending with the OS, re-schedule
          // it against the profile's current briefingTime so a time changed
          // in Settings (or on another device) takes effect. Opt-in stays
          // with the user — nothing is scheduled unless they enabled it.
          (async () => {
            try {
              if (await isBriefingReminderScheduled()) {
                const res = await silentFetch('/me/profile');
                const t = res?.profile?.briefingTime;
                if (t) await scheduleBriefingReminder(t);
              }
            } catch { /* non-fatal */ }
          })();
        }
      } catch (err) {
        console.warn('Native setup error (non-fatal):', err);
      }
    })();
  }, []);
}
